/*
  ITGC Access Requests — table of all access requests with filters.
  Row click opens the ITGCRequestEditorModal (Basic Details — application
  create/edit only; approval & fulfilment are handled elsewhere).
*/
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Plus, RefreshCw, Search, FileEdit, ShieldCheck, PackageCheck, Archive, Inbox } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import Skeleton from '../common/Skeleton';
import {
  itgcService, AccessRequest, RequestStatus,
} from '../../services/itgcService';
import ITGCRequestEditorModal from './ITGCRequestEditorModal';
import ITGCApproveModal from './ITGCApproveModal';
import ITGCFulfillModal from './ITGCFulfillModal';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import Badge from '../common/Badge';
import { statusVariant } from '../../utils/statusVariant';
import './itgc.css';

const ACT_REQUEST = 'ORG_ITGC_ACCESS_REQUEST';
const ACT_APPROVE = 'ORG_ITGC_ACCESS_APPROVE';
const ACT_FULFILL = 'ORG_ITGC_ACCESS_FULFILL';

type RoleFilter = 'all' | 'mine' | 'to_approve' | 'to_fulfil';

const STATUS_OPTIONS: Array<RequestStatus | 'ALL'> = [
  'ALL', 'DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED',
  'FULFILLING', 'CLOSED', 'CANCELLED',
];

/*
  Kanban stages — an access request is in exactly ONE stage at a time, derived
  from its persisted `status`. Each column name reflects what the request is
  WAITING ON:
    Drafts                 — still being authored (DRAFT)
    Pending for Approval   — submitted, awaiting the approver (SUBMITTED)
    Pending for Fulfillment— approved, awaiting/undergoing fulfilment (APPROVED, FULFILLING)
    Closed                 — reached a logical conclusion: fully fulfilled (CLOSED),
                             rejected (REJECTED), or cancelled (CANCELLED)

  --accent drives the column's top border + count chip + card hover (see the
  .bucket-panel / .work-card namespace in global.css).
*/
type StageKey = 'drafts' | 'pending_approval' | 'pending_fulfillment' | 'closed';

interface StageDef {
  key: StageKey;
  title: string;
  icon: React.ReactNode;
  accent: string;                          // CSS colour for --accent
  statuses: RequestStatus[];
}

const STAGES: StageDef[] = [
  { key: 'drafts',              title: 'Drafts',                  icon: <FileEdit size={15} />,    accent: 'var(--text-muted, #8a93a4)',          statuses: ['DRAFT'] },
  { key: 'pending_approval',    title: 'Pending for Approval',    icon: <ShieldCheck size={15} />, accent: 'var(--status-info-text, #2b6cb0)',    statuses: ['SUBMITTED'] },
  { key: 'pending_fulfillment', title: 'Pending for Fulfillment', icon: <PackageCheck size={15} />,accent: 'var(--brand, #6b46c1)',               statuses: ['APPROVED', 'FULFILLING'] },
  { key: 'closed',              title: 'Closed',                  icon: <Archive size={15} />,     accent: 'var(--status-success-text, #2f855a)', statuses: ['CLOSED', 'REJECTED', 'CANCELLED'] },
];

const stageOf = (s: RequestStatus): StageKey =>
  (STAGES.find(st => st.statuses.includes(s)) ?? STAGES[0]).key;


const ITGCAccessManagement: React.FC = () => {
  const { formatDate } = useEntitySettings();
  const reqView    = usePermission(ACT_REQUEST, 'view');
  const aprView    = usePermission(ACT_APPROVE, 'view');
  const fulView    = usePermission(ACT_FULFILL, 'view');
  const canView    = reqView.allowed || aprView.allowed || fulView.allowed;
  const modifyPerm = usePermission(ACT_REQUEST, 'view+modify');
  const canModify  = modifyPerm.allowed;
  const permLoading = modifyPerm.loading;

  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [statusFilter, setStatusFilter] = useState<RequestStatus | 'ALL'>('ALL');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [search, setSearch] = useState('');
  const [editorId, setEditorId] = useState<number | null | undefined>(undefined);
  // which modal to open for a clicked card, keyed by the request's stage
  const [activeModal, setActiveModal] = useState<{ kind: 'approve' | 'fulfill'; id: number } | null>(null);

  const openForRequest = (r: AccessRequest) => {
    if (r.status === 'SUBMITTED') setActiveModal({ kind: 'approve', id: r.id });
    else if (r.status === 'APPROVED' || r.status === 'FULFILLING') setActiveModal({ kind: 'fulfill', id: r.id });
    else {
      // DRAFT (edit) + terminal (read-only view) -> apply modal.
      // The DRAFT path is a write affordance gated on ACT_REQUEST.
      if (r.status === 'DRAFT' && (permLoading || !canModify)) {
        setToast({ type: 'error', message: NOT_AUTHORISED_MSG });
        return;
      }
      setEditorId(r.id);
    }
  };

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [refreshTooltipOpen, setRefreshTooltipOpen] = useState(false);
  const refreshBtnRef = useRef<HTMLButtonElement | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await itgcService.listRequests({
        page: 1, limit: 200,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        role: roleFilter === 'all' ? undefined : roleFilter,
        search: search || undefined,
      });
      setRequests(data);
    } catch (e: any) {
      setToast({ type: 'error', message: e.message || 'Failed to load requests' });
    } finally { setLoading(false); }
  }, [statusFilter, roleFilter, search]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  // Bucket the requests into their (mutually-exclusive) stage columns. The
  // server-side `status` filter already narrows the set; here we just group
  // whatever came back by derived stage.
  const byStage = useMemo(() => {
    const m: Record<StageKey, AccessRequest[]> = {
      drafts: [], pending_approval: [], pending_fulfillment: [], closed: [],
    };
    for (const r of requests) m[stageOf(r.status)].push(r);
    return m;
  }, [requests]);

  if ((!reqView.loading && !aprView.loading && !fulView.loading) && !canView) {
    return (
      <div className="flex flex-col h-full items-center justify-center min-h-[300px]">
        <NoDataFound
          title="Not authorised"
          message={reqView.message || 'You are not authorised to view ITGC Access Rights.'}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">

      {/* ── Toolbar ──────────────────────────────────────────────── */}
      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            value={statusFilter}
            onChange={v => setStatusFilter((v ?? 'ALL') as any)}
            className="filter-status"
            data-testid="itgc-filter-status"
            noSearchIcon
            options={STATUS_OPTIONS.map(s => ({ value: s, label: s }))}
          />
          <FormControl.Select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value as RoleFilter)}
            data-testid="itgc-filter-role"
          >
            <option value="all">All</option>
            <option value="mine">Raised by me</option>
            <option value="to_approve">To approve</option>
            <option value="to_fulfil">To fulfil</option>
          </FormControl.Select>
          <FormControl.Input
            type="text"
            placeholder="Search code / title / justification"
            value={search}
            onChange={e => setSearch(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="itgc-search"
          />

          <Button
            variant="secondary-outline"
            onClick={loadRequests}
            data-testid="itgc-refresh"
          >
            <RefreshCw size={14} />
          </Button>
          <Tooltip
            anchorRef={refreshBtnRef}
            isOpen={refreshTooltipOpen}
            onClose={() => setRefreshTooltipOpen(false)}
            id="itgc-refresh-tooltip"
          >
            Refresh list
          </Tooltip>
          <Button
            onClick={() => {
              if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
              setEditorId(null);
            }}
            disabled={permLoading || !canModify}
            data-testid="itgc-create"
            className={`btn-primary-fill${!canModify ? ' btn--disabled' : ''}`}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
          >
            <Plus />   Create
          </Button>
        </div>
      </div>

      {/* ── Kanban board ─────────────────────────────────────────────
           One column per workflow stage. A request appears in exactly
           one column, derived from its persisted status. */}
      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="bucket-grid bucket-grid--4" data-testid="itgc-kanban-board">
            {STAGES.map(stage => {
              const cards = byStage[stage.key];
              return (
                <div
                  key={stage.key}
                  className="bucket-panel"
                  style={{ ['--accent' as any]: stage.accent }}
                  data-testid={`itgc-kanban-col-${stage.key}`}
                >
                  <div className="bucket-panel-header">
                    <span className="bucket-panel-icon">{stage.icon}</span>
                    <span className="bucket-panel-title">{stage.title}</span>
                    <span className="bucket-panel-count" data-testid={`itgc-kanban-count-${stage.key}`}>
                      {loading ? '—' : cards.length}
                    </span>
                  </div>

                  <div className="bucket-panel-scroll">
                    {loading && (
                      Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="work-card" aria-hidden>
                          <Skeleton.Box width="45%" height={10} />
                          <Skeleton.Box width="80%" height={13} />
                          <Skeleton.Box width="55%" height={10} />
                        </div>
                      ))
                    )}

                    {!loading && cards.length === 0 && (
                      <div className="bucket-empty-state">
                        <Inbox className="bucket-empty-icon" size={22} />
                        <span className="bucket-empty-text">No requests</span>
                      </div>
                    )}

                    {!loading && cards.map(r => (
                      <div
                        key={r.id}
                        className="work-card work-card--clickable"
                        role="button"
                        tabIndex={0}
                        onClick={() => openForRequest(r)}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openForRequest(r); } }}
                        title={r.status === 'DRAFT' && !canModify ? NOT_AUTHORISED_MSG : undefined}
                        data-testid={`itgc-card-${r.id}`}
                      >
                        <div className="work-card-row">
                          <span className="work-card-eyebrow">{r.request_code}</span>
                          <Badge variant={statusVariant(r.status)} size="xs">{r.status}</Badge>
                        </div>
                        <span className="work-card-title">
                          {r.title || <span className="text-gray-400 italic">Untitled request</span>}
                        </span>
                        <span className="work-card-subtitle">
                          {r.initiator_name || `#${r.initiator_user_id}`}
                        </span>
                        {r.submitted_on && (
                          <div className="work-card-footer">
                            <span className="work-card-due">
                              Submitted {formatDate(r.submitted_on)}
                            </span>
                            {r.verifier_name && (
                              <span className="work-card-due">· {r.verifier_name}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {editorId !== undefined && (
        <ITGCRequestEditorModal
          requestId={editorId}
          onClose={() => setEditorId(undefined)}
          onSaved={() => { setEditorId(undefined); loadRequests(); }}
        />
      )}
      {activeModal?.kind === 'approve' && (
        <ITGCApproveModal
          requestId={activeModal.id}
          onClose={() => setActiveModal(null)}
          onSaved={() => { setActiveModal(null); loadRequests(); }}
        />
      )}
      {activeModal?.kind === 'fulfill' && (
        <ITGCFulfillModal
          requestId={activeModal.id}
          onClose={() => setActiveModal(null)}
          onSaved={() => { loadRequests(); }}
        />
      )}
    </div>
  );
};

export default ITGCAccessManagement;
